import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from "react-native";
import FontFamily from "../../../../constants/fonts";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../../theme";

// Define the types for our survey data
export type SurveyItemType =
  | "Header"
  | "Sub-Header"
  | "Data"
  | "Sub-Data"
  | "View";
export type SurveyAnswer =
  | "Yes"
  | "No"
  | "N/A"
  | "Good"
  | "Needs Improvement"
  | "Above Target/On Target"
  | "Below Target"
  | null;

export interface SurveyItem {
  id: string;
  sectionType: SurveyItemType;
  sectionValue: string;
  parentId?: string | null;
  sortOrder: number;
  section: number;
  probation30?: number | null;
  probation60?: number | null;
  probation90?: number | null;
  preferredMethod?: number | null;
}

interface SurveyProps {
  items: SurveyItem[];
  observationType?: string; // Add observationType to determine if it's preferred method
  onAnswerChange?: (answers: Record<string, SurveyAnswer>) => void;
  onCommentChange?: (comments: Record<string, string>) => void;
  initialAnswers?: Record<string, SurveyAnswer>;
  initialComments?: Record<string, string>;
  isViewOnlyMode?: boolean;
  testID?: string;
}

interface RadioButtonProps {
  selected: boolean;
  onPress: () => void;
  label: string;
  disabled?: boolean;
  testID?: string;
}

// Radio button component
const RadioButton: React.FC<RadioButtonProps> = ({
  selected,
  onPress,
  label,
  disabled = false,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  return (
    <TouchableOpacity
      style={[
        styles.radioButtonContainer,
        disabled && styles.radioButtonContainerDisabled,
      ]}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      testID={testID}
    >
      <View style={styles.radioButtonWrapper}>
        <View
          style={[
            styles.radioButton,
            isDarkTheme && { borderColor: colors.text.primary },
            disabled && styles.radioButtonDisabled,
          ]}
        >
          {selected && (
            <View
              style={[
                styles.radioButtonSelected,
                isDarkTheme && { backgroundColor: colors.text.primary },
                disabled && styles.radioButtonSelectedDisabled,
              ]}
            />
          )}
        </View>
      </View>
      <Text
        style={[
          styles.radioButtonLabel,
          isDarkTheme && { color: colors.text.primary },
          disabled && styles.radioButtonLabelDisabled,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

// Question row component to render a single survey item with radio buttons
const QuestionRow: React.FC<{
  item: SurveyItem;
  answer: SurveyAnswer;
  onSelectAnswer: (id: string, answer: SurveyAnswer) => void;
  isSubData?: boolean;
  sectionNumber: number;
  observationType?: string;
  isViewOnlyMode?: boolean;
  testID?: string;
}> = ({
  item,
  answer,
  onSelectAnswer,
  isSubData = false,
  sectionNumber,
  observationType,
  isViewOnlyMode = false,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Get answer options based on section number and observation type
  const getAnswerOptions = (section: number, obsType?: string) => {
    // For preferred method, always use Yes/No/N/A
    if (obsType === "preferred_method") {
      return [
        { value: "Yes" as SurveyAnswer, label: "Yes" },
        { value: "No" as SurveyAnswer, label: "No" },
        { value: "N/A" as SurveyAnswer, label: "N/A" },
      ];
    }

    // For other observation types, use section-specific options
    switch (section) {
      case 1:
        return [
          { value: "Yes" as SurveyAnswer, label: "Yes" },
          { value: "No" as SurveyAnswer, label: "No" },
          { value: "N/A" as SurveyAnswer, label: "N/A" },
        ];
      case 2:
        return [
          { value: "Yes" as SurveyAnswer, label: "Yes" },
          { value: "No" as SurveyAnswer, label: "No" },
          { value: "N/A" as SurveyAnswer, label: "N/A" },
        ];
      case 3:
        return [
          {
            value: "Above Target/On Target" as SurveyAnswer,
            label: "Above Target/On Target",
          },
          { value: "Below Target" as SurveyAnswer, label: "Below Target" },
          { value: "N/A" as SurveyAnswer, label: "N/A" },
        ];
      default:
        return [
          { value: "Yes" as SurveyAnswer, label: "Yes" },
          { value: "No" as SurveyAnswer, label: "No" },
          { value: "N/A" as SurveyAnswer, label: "N/A" },
        ];
    }
  };

  const answerOptions = getAnswerOptions(sectionNumber, observationType);

  return (
    <View
      style={[
        styles.questionRow,
        isDarkTheme && { borderBottomColor: colors.border.divider },
      ]}
      testID={`${testID}-row-${item.id}`}
    >
      <View style={styles.questionTextContainer}>
        <Text style={[styles.questionText, isDarkTheme && { color: colors.text.primary }]}>
          {item.sectionValue}
        </Text>
      </View>
      <View style={styles.radioButtonsContainer}>
        {answerOptions.map((option, index) => (
          <RadioButton
            key={index}
            selected={answer === option.value}
            onPress={() => onSelectAnswer(item.id, option.value)}
            label=""
            disabled={isViewOnlyMode}
            testID={`${testID}-${
              option.value
                ? option.value.toLowerCase().replace(/[^a-z0-9]/g, "-")
                : "unknown"
            }-${item.id}`}
          />
        ))}
      </View>
    </View>
  );
};

// Column headers component with dynamic headers based on section
const ColumnHeaders: React.FC<{
  sectionNumber: number;
  observationType?: string;
  testID?: string;
}> = ({ sectionNumber, observationType, testID }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Get column headers based on section number and observation type
  const getColumnHeaders = (section: number, obsType?: string) => {
    // For preferred method, always use Yes/No/N/A
    if (obsType === "preferred_method") {
      return ["Yes", "No", "N/A"];
    }

    // For other observation types, use section-specific headers
    switch (section) {
      case 1:
        return ["Yes", "No", "N/A"];
      case 2:
        return ["Yes", "No", "N/A"];
      case 3:
        return ["Above Target/On Target", "Below Target", "N/A"];
      default:
        return ["Yes", "No", "N/A"];
    }
  };

  const headers = getColumnHeaders(sectionNumber, observationType);

  return (
    <View
      style={[
        styles.columnHeadersContainer,
        isDarkTheme && { borderBottomColor: colors.border.divider },
      ]}
      testID={`${testID}-headers`}
    >
      <View style={styles.questionTextContainer}>
        <Text style={styles.columnHeaderText} />
      </View>
      <View style={styles.radioButtonsContainer}>
        {headers.map((header, index) => (
          <View key={index} style={styles.columnHeaderItem}>
            <Text
              style={[
                styles.columnHeaderText,
                isDarkTheme
                  ? { color: colors.text.primary }
                  : { color: colors.text.secondary },
              ]}
            >
              {header}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

// Comment box component for sections
const CommentBox: React.FC<{
  sectionNumber: number;
  value: string;
  onChangeText: (text: string) => void;
  disabled?: boolean;
  testID?: string;
}> = ({ sectionNumber, value, onChangeText, disabled = false, testID }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const getPlaceholderText = (section: number): string => {
    switch (section) {
      case 1:
        return "Additional comments or support needed for policy understanding and expectations...";
      case 3:
        return "Additional comments on performance metrics and targets...";
      default:
        return "Additional comments...";
    }
  };

  return (
    <View style={styles.commentBoxContainer} testID={testID}>
      <Text
        style={[
          styles.commentBoxLabel,
          isDarkTheme
            ? { color: colors.text.primary }
            : { color: colors.text.secondary },
        ]}
      >
        Comments:
      </Text>
      <TextInput
        style={[
          styles.commentBoxInput,
          isDarkTheme
            ? {
                color: colors.text.primary,
                backgroundColor: colors.background.elevated,
                borderColor: colors.border.default,
              }
            : { color: "#2D2D2D" },
          disabled && styles.commentBoxInputDisabled,
          disabled &&
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
              color: colors.text.secondary,
            },
        ]}
        multiline={true}
        numberOfLines={4}
        placeholder={getPlaceholderText(sectionNumber)}
        placeholderTextColor={isDarkTheme ? colors.text.secondary : "#6C757D"}
        value={value}
        onChangeText={disabled ? undefined : onChangeText}
        editable={!disabled}
        textAlignVertical="top"
        testID={`${testID}-input`}
      />
    </View>
  );
};

// Section info message component
const SectionInfoMessage: React.FC<{
  sectionNumber: number;
  observationType?: string;
  testID?: string;
}> = ({ sectionNumber, observationType, testID }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const getSectionMessage = (section: number, obsType?: string): string => {
    switch (section) {
      case 1:
        return "Review each section with the employee. Check for understanding of policy & expectations and/or support needed from the employee";
      case 2:
        // Don't show Section 2 message for preferred method
        if (obsType === "preferred_method") {
          return "";
        }
        return "Review current performance with the employee, set expectations, and provide feedback\nYes - the employee has NOT had any violations of policy or expectations\nNo - the employee has had one or more violations of the policy or expectations\nN/A - this policy does not apply to your location";
      case 3:
        // Don't show Section 3 message for preferred method
        if (obsType === "preferred_method") {
          return "";
        }
        return "Current Performance Metrics - Attach Employee Performance Form from Tableau\nUtilize location specific probationary period performance targets if different from national targets";
      default:
        return "";
    }
  };

  const message = getSectionMessage(sectionNumber, observationType);

  if (!message) {
    return null;
  }

  return (
    <View
      style={[
        styles.sectionInfoMessage,
        isDarkTheme
          ? {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            }
          : {
              backgroundColor: "#f5f5f5",
              borderColor: "#034895",
            },
      ]}
      testID={testID}
    >
      <MaterialIcons
        name="info-outline"
        size={20}
        color={isDarkTheme ? colors.text.primary : "#316BB8"}
        style={{ marginRight: 8 }}
      />
      <Text
        style={[
          styles.sectionInfoText,
          isDarkTheme
            ? { color: colors.text.primary }
            : { color: colors.text.secondary },
        ]}
      >
        {message}
      </Text>
    </View>
  );
};

// Header component
const SectionHeader: React.FC<{ title: string; testID?: string }> = ({
  title,
  testID,
}) => {
  const { colors } = useTheme();
  return (
  <View style={[styles.sectionHeader, { backgroundColor: colors.brand.primary }]} testID={testID}>
    <Text style={styles.sectionHeaderText}>{title}</Text>
  </View>
  );
};

// Sub-header component with proper indentation
const SubHeader: React.FC<{ title: string; testID?: string }> = ({
  title,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  return (
  <View
    style={[
      styles.subHeader,
      isDarkTheme && {
        backgroundColor: colors.background.elevated,
        borderColor: colors.border.default,
        borderWidth: 1,
      },
    ]}
    testID={testID}
  >
    <Text
      style={[
        styles.subHeaderText,
        isDarkTheme && { color: colors.text.primary },
      ]}
    >
      {title}
    </Text>
  </View>
  );
};

// Informational View Item component (no radio buttons)
const ViewItem: React.FC<{ item: SurveyItem; testID?: string }> = ({
  item,
  testID,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  return (
  <View style={styles.viewItem} testID={testID}>
    <Text
      style={[
        styles.viewItemText,
        isDarkTheme
          ? { color: colors.text.primary }
          : { color: colors.text.secondary },
      ]}
    >
      {item.sectionValue}
    </Text>
  </View>
  );
};

// Informational Group component for Headers with only View children
const InformationalGroup: React.FC<{
  group: any;
  testID?: string;
}> = ({ group, testID }) => (
  <InformationalGroupContent group={group} testID={testID} />
);

const InformationalGroupContent: React.FC<{ group: any; testID?: string }> = ({ group, testID }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  return (
  <View
    style={[
      styles.informationalGroup,
      isDarkTheme
        ? {
            backgroundColor: colors.background.elevated,
            borderColor: colors.border.default,
          }
        : {
            backgroundColor: "#E6F3EE",
            borderColor: "#008553",
          },
    ]}
    testID={testID}
  >
    <View style={styles.informationalHeader}>
      <MaterialIcons
        name="check-circle-outline"
        size={20}
        color={isDarkTheme ? colors.text.primary : "#008553"}
        style={{ marginRight: 8 }}
      />
      <Text
        style={[
          styles.informationalHeaderText,
          isDarkTheme
            ? { color: colors.text.primary }
            : { color: colors.text.secondary },
        ]}
      >
        {group.header.sectionValue}
      </Text>
    </View>
    {group.viewItems.map((item: SurveyItem) => (
      <ViewItem
        key={item.id}
        item={item}
        testID={`${testID}-view-${item.id}`}
      />
    ))}
  </View>
  );
};

// Main survey component
const TouchPointSurvey: React.FC<SurveyProps> = ({
  items,
  observationType,
  onAnswerChange,
  onCommentChange,
  initialAnswers = {},
  initialComments = {},
  isViewOnlyMode = false,
  testID = "touchpoint-survey",
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // State to track selected answers
  const [answers, setAnswers] =
    useState<Record<string, SurveyAnswer>>(initialAnswers);

  // State to track section comments
  const [comments, setComments] =
    useState<Record<string, string>>(initialComments);

  // State to track single comment for preferred method
  const [singleComment, setSingleComment] = useState<string>(
    initialComments["single-comment"] || ""
  );

  // Sync comments when initialComments prop changes (for pre-filling)
  useEffect(() => {
    console.log(
      "TouchPointSurvey: Syncing comments with initialComments:",
      initialComments
    );
    setComments(initialComments);
    setSingleComment(initialComments["single-comment"] || "");
  }, [initialComments]);

  // Sync answers when initialAnswers prop changes (for pre-filling)
  useEffect(() => {
    console.log(
      "TouchPointSurvey: Syncing answers with initialAnswers:",
      initialAnswers
    );
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  // Process and organize survey items by section
  const processedSections = React.useMemo(() => {
    // Helper function to check if a header has only "View" type children
    const hasOnlyViewChildren = (
      headerId: string,
      allItems: SurveyItem[]
    ): boolean => {
      const children = allItems.filter((item) => item.parentId === headerId);
      return (
        children.length > 0 &&
        children.every((child) => child.sectionType === "View")
      );
    };

    // First, extract all informational groups (Headers with only View children) across all sections
    const allInformationalGroups: any[] = [];
    items.forEach((item) => {
      if (
        item.sectionType === "Header" &&
        hasOnlyViewChildren(item.id, items)
      ) {
        const viewChildren = items.filter(
          (child) => child.parentId === item.id && child.sectionType === "View"
        );

        allInformationalGroups.push({
          header: item,
          viewItems: viewChildren,
          isInformational: true,
          originalSection: item.section,
        });
      }
    });

    // Filter out informational content and group remaining items by section number
    const informationalHeaderIds = new Set(
      allInformationalGroups.map((group) => group.header.id)
    );
    const informationalViewIds = new Set(
      allInformationalGroups.flatMap((group) =>
        group.viewItems.map((item: SurveyItem) => item.id)
      )
    );

    const surveyItems = items.filter(
      (item) =>
        !informationalHeaderIds.has(item.id) &&
        !informationalViewIds.has(item.id)
    );

    // Group survey items by section number
    const sectionGroups: Record<number, SurveyItem[]> = {};
    surveyItems.forEach((item) => {
      if (!sectionGroups[item.section]) {
        sectionGroups[item.section] = [];
      }
      sectionGroups[item.section].push(item);
    });

    // Process each section (only for actual survey content)
    const sections = Object.keys(sectionGroups)
      .filter((sectionKey) => sectionGroups[parseInt(sectionKey)].length > 0) // Only include sections with content
      .map((sectionKey) => {
        const sectionNumber = parseInt(sectionKey);
        let sectionItems = sectionGroups[sectionNumber];

        // Sort items within each section to maintain proper hierarchical order
        sectionItems = sectionItems.sort((a, b) => {
          // First sort by Sort_Order
          if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
          }

          // For items with the same Sort_Order, we need to maintain hierarchy:
          // 1. Headers first
          // 2. Sub-Headers under their parent Headers
          // 3. Sub-Data under their parent Sub-Headers
          // 4. Data items under their parent Headers (but after Sub-Headers and Sub-Data)

          // If both items have the same parent, sort by type hierarchy
          if (a.parentId === b.parentId) {
            const typeOrder = {
              Header: 1,
              "Sub-Header": 2,
              "Sub-Data": 3,
              Data: 4,
              View: 5,
            };

            const aTypeOrder = typeOrder[a.sectionType] || 6;
            const bTypeOrder = typeOrder[b.sectionType] || 6;

            if (aTypeOrder !== bTypeOrder) {
              return aTypeOrder - bTypeOrder;
            }
          }

          // Handle parent-child relationships
          // If 'a' is a parent of 'b', 'a' should come first
          if (a.id === b.parentId) {
            return -1;
          }
          if (b.id === a.parentId) {
            return 1;
          }

          // If 'a' is a child and 'b' is not its parent, check if they have different parents
          if (a.parentId && b.parentId && a.parentId !== b.parentId) {
            // Find the parent items and compare their order
            const aParent = sectionItems.find((item) => item.id === a.parentId);
            const bParent = sectionItems.find((item) => item.id === b.parentId);

            if (aParent && bParent) {
              // If parents have different sort orders, use that
              if (aParent.sortOrder !== bParent.sortOrder) {
                return aParent.sortOrder - bParent.sortOrder;
              }
              // If parents have the same sort order, compare their IDs
              return parseInt(aParent.id) - parseInt(bParent.id);
            }
          }

          // Finally sort by ID to maintain consistent order
          return parseInt(a.id) - parseInt(b.id);
        });

        console.log(
          `TouchPointSurvey: Processing Survey Section ${sectionNumber} with ${sectionItems.length} items:`,
          sectionItems
        );

        // Group items by header within this section
        const headerGroups: Record<
          string,
          {
            header: SurveyItem;
            subHeaders: Record<
              string,
              {
                subHeader: SurveyItem;
                questions: SurveyItem[];
              }
            >;
            questions: SurveyItem[];
          }
        > = {};

        // First pass: identify headers and sub-headers
        sectionItems.forEach((item) => {
          if (item.sectionType === "Header") {
            headerGroups[item.id] = {
              header: item,
              subHeaders: {},
              questions: [],
            };
          } else if (
            item.sectionType === "Sub-Header" &&
            item.parentId &&
            headerGroups[item.parentId]
          ) {
            headerGroups[item.parentId].subHeaders[item.id] = {
              subHeader: item,
              questions: [],
            };
          }
        });

        // Second pass: assign questions to their respective containers
        sectionItems.forEach((item) => {
          if (item.sectionType === "Data" || item.sectionType === "Sub-Data") {
            if (item.parentId) {
              // Check if it's a direct child of a header
              if (headerGroups[item.parentId]) {
                if (item.sectionType === "Data") {
                  headerGroups[item.parentId].questions.push(item);
                }
              } else {
                // Check if it's a child of a sub-header
                for (const headerId in headerGroups) {
                  const subHeaders = headerGroups[headerId].subHeaders;
                  if (subHeaders[item.parentId]) {
                    subHeaders[item.parentId].questions.push(item);
                    break;
                  }
                }
              }
            } else {
              // Handle items without parentId (like Section 3 direct data items)
              // Create a temporary header group for orphaned items
              const orphanedHeaderId = `section-${sectionNumber}-orphaned`;
              if (!headerGroups[orphanedHeaderId]) {
                // For Section 3, we don't want to show a generic header, so we'll create a special case
                const headerTitle =
                  sectionNumber === 3
                    ? "Performance Metrics"
                    : `Section ${sectionNumber} Items`;
                headerGroups[orphanedHeaderId] = {
                  header: {
                    id: orphanedHeaderId,
                    sectionType: "Header",
                    sectionValue: headerTitle,
                    parentId: null,
                    sortOrder: 999,
                    section: sectionNumber,
                    probation30: null,
                    probation60: null,
                    probation90: null,
                    preferredMethod: null,
                  },
                  subHeaders: {},
                  questions: [],
                };
              }
              headerGroups[orphanedHeaderId].questions.push(item);
            }
          }
        });

        // Convert to section format
        const headerSections = Object.values(headerGroups)
          .sort((a, b) => {
            // Sort headers by their sortOrder, then by ID
            if (a.header.sortOrder !== b.header.sortOrder) {
              return a.header.sortOrder - b.header.sortOrder;
            }
            return parseInt(a.header.id) - parseInt(b.header.id);
          })
          .map((headerGroup) => {
            // Sort sub-headers by their sortOrder and ID
            const subHeaderSections = Object.values(headerGroup.subHeaders)
              .sort((a, b) => {
                if (a.subHeader.sortOrder !== b.subHeader.sortOrder) {
                  return a.subHeader.sortOrder - b.subHeader.sortOrder;
                }
                return parseInt(a.subHeader.id) - parseInt(b.subHeader.id);
              })
              .map((subHeaderGroup) => ({
                title: subHeaderGroup.subHeader.sectionValue,
                data: subHeaderGroup.questions.sort((a, b) => {
                  // Sort questions within sub-header by sortOrder, then by ID
                  if (a.sortOrder !== b.sortOrder) {
                    return a.sortOrder - b.sortOrder;
                  }
                  return parseInt(a.id) - parseInt(b.id);
                }),
                type: "sub-header",
                id: subHeaderGroup.subHeader.id,
                sortOrder: subHeaderGroup.subHeader.sortOrder,
              }));

            return {
              title: headerGroup.header.sectionValue,
              id: headerGroup.header.id,
              data: headerGroup.questions.sort((a, b) => {
                // Sort direct questions under header by sortOrder, then by ID
                if (a.sortOrder !== b.sortOrder) {
                  return a.sortOrder - b.sortOrder;
                }
                return parseInt(a.id) - parseInt(b.id);
              }),
              subSections: subHeaderSections,
              isInformational: false,
              sortOrder: headerGroup.header.sortOrder,
            };
          });

        return {
          sectionNumber,
          headers: headerSections,
          hasInformationalGroups: false,
          informationalGroups: [],
        };
      })
      .sort((a, b) => a.sectionNumber - b.sectionNumber);

    console.log(
      "TouchPointSurvey: All Informational Groups:",
      allInformationalGroups
    );
    console.log("TouchPointSurvey: Final processed survey sections:", sections);

    return {
      informationalGroups: allInformationalGroups,
      surveySections: sections,
    };
  }, [items]);

  // Handler for answer selection
  const handleSelectAnswer = useCallback(
    (id: string, answer: SurveyAnswer) => {
      setAnswers((prevAnswers) => {
        const newAnswers = { ...prevAnswers, [id]: answer };

        // Call the callback if provided
        if (onAnswerChange) {
          onAnswerChange(newAnswers);
        }

        return newAnswers;
      });
    },
    [onAnswerChange]
  );

  // Handler for comment changes
  const handleCommentChange = useCallback(
    (sectionNumber: number, text: string) => {
      setComments((prevComments) => {
        const newComments = {
          ...prevComments,
          [`section-${sectionNumber}`]: text,
        };

        // Call the callback if provided
        if (onCommentChange) {
          onCommentChange(newComments);
        }

        return newComments;
      });
    },
    [onCommentChange]
  );

  // Handler for single comment change (for preferred method)
  const handleSingleCommentChange = useCallback(
    (text: string) => {
      setSingleComment(text);

      // Call the callback if provided with the single comment
      if (onCommentChange) {
        const newComments = { ...comments, "single-comment": text };
        onCommentChange(newComments);
      }
    },
    [onCommentChange, comments]
  );

  // Render a section with its headers and questions
  const renderSection = useCallback(
    (sectionData: any) => {
      return (
        <View
          key={sectionData.sectionNumber}
          testID={`${testID}-section-${sectionData.sectionNumber}`}
        >
          {/* Section info message */}
          <SectionInfoMessage
            sectionNumber={sectionData.sectionNumber}
            observationType={observationType}
            testID={`${testID}-info-${sectionData.sectionNumber}`}
          />

          {/* Render all headers in this section */}
          {sectionData.headers.map((section: any) => (
            <View
              key={section.id}
              testID={`${testID}-header-group-${section.id}`}
            >
              <SectionHeader
                title={section.title}
                testID={`${testID}-header-${section.id}`}
              />
              {/* Only show column headers if there are questions in this section or its subsections */}
              {(section.data.length > 0 ||
                section.subSections.some(
                  (sub: any) => sub.data.length > 0
                )) && (
                <ColumnHeaders
                  sectionNumber={sectionData.sectionNumber}
                  observationType={observationType}
                  testID={`${testID}-column-headers-${section.id}`}
                />
              )}

              {/* Render sub-sections with their questions FIRST */}
              {section.subSections.map((subSection: any) => (
                <View key={subSection.id}>
                  <SubHeader
                    title={subSection.title}
                    testID={`${testID}-subheader-${subSection.id}`}
                  />
                  {/* Only show column headers if there are questions in this subsection */}
                  {subSection.data.length > 0 && (
                    <ColumnHeaders
                      sectionNumber={sectionData.sectionNumber}
                      observationType={observationType}
                      testID={`${testID}-column-headers-sub-${subSection.id}`}
                    />
                  )}

                  {subSection.data.map((item: SurveyItem) => (
                    <QuestionRow
                      key={item.id}
                      item={item}
                      answer={answers[item.id] || null}
                      onSelectAnswer={handleSelectAnswer}
                      isSubData={item.sectionType === "Sub-Data"}
                      sectionNumber={sectionData.sectionNumber}
                      observationType={observationType}
                      isViewOnlyMode={isViewOnlyMode}
                      testID={`${testID}-sub-question`}
                    />
                  ))}
                </View>
              ))}

              {/* Render direct questions under this header AFTER sub-sections */}
              {section.data.map((item: SurveyItem) => (
                <QuestionRow
                  key={item.id}
                  item={item}
                  answer={answers[item.id] || null}
                  onSelectAnswer={handleSelectAnswer}
                  sectionNumber={sectionData.sectionNumber}
                  observationType={observationType}
                  isViewOnlyMode={isViewOnlyMode}
                  testID={`${testID}-question`}
                />
              ))}
            </View>
          ))}

          {/* Add comment box for sections 1 and 2 (only for non-preferred method) */}
          {observationType !== "preferred_method" &&
            (sectionData.sectionNumber === 1 ||
              sectionData.sectionNumber === 2) && (
              <CommentBox
                sectionNumber={sectionData.sectionNumber}
                value={comments[`section-${sectionData.sectionNumber}`] || ""}
                onChangeText={(text) =>
                  handleCommentChange(sectionData.sectionNumber, text)
                }
                disabled={isViewOnlyMode}
                testID={`${testID}-comment-section-${sectionData.sectionNumber}`}
              />
            )}
        </View>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      answers,
      comments,
      handleSelectAnswer,
      handleCommentChange,
      observationType,
      testID,
    ]
  );

  return (
    <View
      style={[
        styles.container,
        isDarkTheme
          ? { backgroundColor: colors.background.surface }
          : { backgroundColor: "#fff" },
      ]}
      testID={testID}
    >
      {processedSections.surveySections.length === 0 &&
      processedSections.informationalGroups.length === 0 ? (
        <Text
          style={[
            styles.noDataText,
            isDarkTheme
              ? { color: colors.text.primary }
              : { color: colors.text.secondary },
          ]}
        >
          No survey questions available.
        </Text>
      ) : (
        <View
          style={[
            styles.listContainer,
            isDarkTheme && { backgroundColor: colors.background.surface },
          ]}
        >
          {/* Render all informational groups first */}
          {processedSections.informationalGroups.map(
            (group: any, index: number) => (
              <InformationalGroup
                key={`info-global-${group.header.id}`}
                group={group}
                testID={`${testID}-global-info-group-${index}`}
              />
            )
          )}

          {/* Add separator if we have both informational groups and survey sections */}
          {/* {processedSections.informationalGroups.length > 0 &&
            processedSections.surveySections.length > 0 && (
              <View style={styles.contentSeparator}>
                <View style={styles.contentSeparatorLine} />
                <Text style={styles.contentSeparatorText}>Survey Sections</Text>
                <View style={styles.contentSeparatorLine} />
              </View>
            )} */}

          {/* Render survey sections */}
          {processedSections.surveySections.map((sectionData: any) =>
            renderSection(sectionData)
          )}

          {/* Add single comment box at the end for preferred method */}
          {observationType === "preferred_method" &&
            processedSections.surveySections.length > 0 && (
              <View style={styles.singleCommentContainer}>
                <Text style={styles.singleCommentLabel}>
                  Additional Comments:
                </Text>
                <TextInput
                  style={[
                    styles.singleCommentInput,
                    isDarkTheme
                      ? {
                          color: colors.text.primary,
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                        }
                      : {
                          color: "#2D2D2D",
                          backgroundColor: "#fff",
                        },
                    isViewOnlyMode && styles.singleCommentInputDisabled,
                    isViewOnlyMode &&
                      isDarkTheme && {
                        color: colors.text.secondary,
                        backgroundColor: colors.background.surface,
                        borderColor: colors.border.default,
                      },
                  ]}
                  multiline={true}
                  numberOfLines={4}
                  placeholder="Additional comments about the touchpoint survey..."
                  placeholderTextColor={isDarkTheme ? colors.text.secondary : "#999"}
                  value={singleComment}
                  onChangeText={
                    isViewOnlyMode ? undefined : handleSingleCommentChange
                  }
                  editable={!isViewOnlyMode}
                  textAlignVertical="top"
                  testID={`${testID}-single-comment-input`}
                />
              </View>
            )}
        </View>
      )}
    </View>
  );
};

// Styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  listContainer: {
    padding: 16,
  },
  noDataText: {
    padding: 16,
    textAlign: "center",
    color: "#3A3A3A",
    fontSize: 16,
  },
  sectionHeader: {
    backgroundColor: "#034895",
    padding: 12,
    borderRadius: 4,
    marginVertical: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  sectionHeaderText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "600",
  },
  sectionInfoMessage: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 4,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: "#034895",
  },
  sectionInfoText: {
    color: "#3A3A3A",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    fontFamily: FontFamily.regular,
    //fontStyle: "italic",
  },
  subHeader: {
    backgroundColor: "#e6f2ff",
    padding: 10,
    borderRadius: 4,
    marginTop: 12,
    marginBottom: 8,
    marginLeft: 16, // Add indentation for sub-headers
  },
  subHeaderText: {
    color: "#034895",
    fontSize: 16,
    fontWeight: "500",
  },
  columnHeadersContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e1e1e1",
    paddingVertical: 8,
  },
  columnHeaderText: {
    fontWeight: "600",
    color: "#616161",
    fontSize: 12, // Reduced font size to fit longer text
    textAlign: "center",
    lineHeight: 16,
  },
  columnHeaderItem: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 2, // Add horizontal padding for better spacing
  },
  questionRow: {
    flexDirection: "row",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e9e9e9",
    alignItems: "center",
  },
  questionTextContainer: {
    flex: 3,
    paddingRight: 8,
  },
  questionText: {
    fontSize: 15,
    color: "#3A3A3A",
  },
  radioButtonsContainer: {
    flex: 2,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  radioButtonContainer: {
    flex: 1,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  radioButtonWrapper: {
    alignItems: "center",
  },
  radioButton: {
    height: 20,
    width: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#0066cc",
    alignItems: "center",
    justifyContent: "center",
  },
  radioButtonSelected: {
    height: 10,
    width: 10,
    borderRadius: 5,
    backgroundColor: "#0066cc",
  },
  radioButtonLabel: {
    fontSize: 14,
    color: "#3A3A3A",
    marginLeft: 5,
  },
  // Disabled radio button styles
  radioButtonContainerDisabled: {
    opacity: 1,
  },
  radioButtonDisabled: {
    borderColor: "#999999",
  },
  radioButtonSelectedDisabled: {
    backgroundColor: "#999999",
  },
  radioButtonLabelDisabled: {
    color: "#999999",
  },
  // Comment box styles
  commentBoxContainer: {
    marginTop: 16,
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  commentBoxLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#3A3A3A",
    marginBottom: 8,
  },
  commentBoxInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    color: "#2D2D2D",
    borderRadius: 4,
    padding: 12,
    fontSize: 14,
    backgroundColor: "#fff",
    minHeight: 80,
    maxHeight: 120,
  },
  commentBoxInputDisabled: {
    backgroundColor: "#f5f5f5",
    color: "#6C757D",
    borderColor: "#e0e0e0",
  },
  // Informational group styles
  informationalGroup: {
    backgroundColor: "#E6F3EE",
    borderRadius: 4,
    padding: 12,
    marginVertical: 8,
    //borderLeftWidth: 4,
    //borderLeftColor: "#28a745",
    borderWidth:1,
    borderColor:"#008553",
    //paddingLeft:10,
  },
  informationalHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    //paddingLeft: 10,
  },
  informationalHeaderText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#3A3A3A",
  },
  viewItem: {
    paddingVertical: 6,
    paddingLeft: 12,
  },
  viewItemText: {
    fontSize: 14,
    color: "#3A3A3A",
    lineHeight: 20,
  },
  // Content separator styles
  contentSeparator: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 24,
    paddingHorizontal: 16,
  },
  contentSeparatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#ccc",
  },
  contentSeparatorText: {
      color: "#3A3A3A",
    marginHorizontal: 16,
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  // Single comment box styles (for preferred method)
  singleCommentContainer: {
    marginTop: 24,
    marginBottom: 16,
    paddingHorizontal: 4,
    borderTopWidth: 2,
    borderTopColor: "#034895",
    paddingTop: 16,
  },
  singleCommentLabel: {
    fontSize: 12,
    fontWeight: "400",
    color: "#3A3A3A",
    marginBottom: 12,
  },
  singleCommentInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    color: "#2D2D2D",
    borderRadius: 6,
    padding: 16,
    fontSize: 15,
    backgroundColor: "#fff",
    minHeight: 100,
    maxHeight: 150,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2,
  },
  singleCommentInputDisabled: {
    backgroundColor: "#f5f5f5",
    color: "#6C757D",
    borderColor: "#e0e0e0",
  },
});

export default TouchPointSurvey;
